import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../data";
import { useLang, STR } from "../i18n";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export const Gallery = () => {
  const [idx, setIdx] = useState(null);
  const [colCount, setColCount] = useState(4);
  const { lang } = useLang();
  const t = STR[lang].gallery;

  useEffect(() => {
    const update = () => setColCount(window.innerWidth < 640 ? 2 : window.innerWidth < 1024 ? 3 : 4);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const columns = useMemo(() => {
    const arr = Array.from({ length: colCount }, () => ({ items: [], height: 0 }));
    GALLERY.forEach((item, i) => {
      const shortest = arr.reduce((a, b) => (a.height <= b.height ? a : b));
      shortest.items.push({ src: item.src, i });
      shortest.height += item.ar;
    });
    return arr.map((c) => c.items);
  }, [colCount]);

  const close = () => setIdx(null);
  const prev = () => setIdx((i) => (i - 1 + GALLERY.length) % GALLERY.length);
  const next = () => setIdx((i) => (i + 1) % GALLERY.length);

  useEffect(() => {
    if (idx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx]);

  return (
    <section id="gallery" className="bg-[#f8fafb] pt-[72px] pb-[80px]" data-testid="gallery-section">
      <div className="max-w-[1320px] mx-auto px-6">
        <motion.div {...reveal} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-[25px]">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#F18A00] font-bold" data-testid="gallery-eyebrow">
              {t.eyebrow}
            </p>
            <h2 className="font-display font-medium text-[30px] tracking-[-0.02em] text-[#0D1B2A] mt-[7px]" data-testid="gallery-title">
              {t.title}
            </h2>
            <span className="block w-[38px] h-0.5 bg-[#F18A00] mt-[14px]" aria-hidden="true" />
          </div>
          <p className="text-xs text-[#69747c] md:text-right" data-testid="gallery-intro">
            {t.intro}
          </p>
        </motion.div>

        <motion.div {...reveal} className="flex gap-[13px] items-start" data-testid="gallery-grid">
          {columns.map((col, c) => (
            <div key={c} className="flex-1 flex flex-col gap-[13px] min-w-0">
              {col.map(({ src, i }) => (
                <button
                  key={src}
                  onClick={() => setIdx(i)}
                  className="block w-full overflow-hidden group cursor-zoom-in"
                  data-testid={`gallery-item-${i}`}
                  aria-label={`Apri foto ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`Project Works — foto ${i + 1}`}
                    loading="lazy"
                    className="w-full transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          ))}
        </motion.div>
      </div>

      <AnimatePresence>
        {idx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-[#0D1B2A]/95 flex items-center justify-center p-6"
            onClick={close}
            data-testid="gallery-lightbox"
            role="dialog"
            aria-modal="true"
          >
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-[#F18A00] transition-colors z-10"
              onClick={close}
              aria-label="Chiudi"
              data-testid="gallery-lightbox-close"
            >
              <X size={32} strokeWidth={1.25} />
            </button>
            <button
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#F18A00] transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Foto precedente"
              data-testid="gallery-lightbox-prev"
            >
              <ChevronLeft size={44} strokeWidth={1.25} />
            </button>
            <button
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#F18A00] transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Foto successiva"
              data-testid="gallery-lightbox-next"
            >
              <ChevronRight size={44} strokeWidth={1.25} />
            </button>
            <motion.figure
              key={idx}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-[92vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[idx].src}
                alt={`Project Works — foto ${idx + 1}`}
                className="max-h-[82vh] max-w-full object-contain"
                data-testid="gallery-lightbox-image"
              />
              <figcaption className="mt-4 text-center text-[11px] uppercase tracking-[0.2em] text-white/60" data-testid="gallery-lightbox-counter">
                {idx + 1} / {GALLERY.length}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
